import { Divider } from "@msuite/ui/divider";
import { FormSwitch, NumberInput, Select, TextInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { IMAP_PRESETS } from "@msuite/utils/mailboxes";
import { MAILBOX_IMAP_SECURITIES, MAILBOX_POLLING_INTERVALS } from "@msuite/utils/schemas";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import type { MailboxFormValues } from "./mailbox-form-values";

/** Props Interface */
interface MailboxFormProps {
	/** Whether the form edits a connected mailbox: the password becomes optional, pause appears. */
	isEdit: boolean;
}

/** The port each security mode implies, so switching one moves the other along. */
const DEFAULT_PORT: Record<MailboxFormValues["imapSecurity"], number> = { tls: 993, starttls: 143 };

export function MailboxForm({ isEdit }: MailboxFormProps) {
	/** Context */
	const methods = useFormContext<MailboxFormValues>();

	/** Hooks */
	const { t } = useTranslation();
	const emailAddress = useWatch({ control: methods.control, name: "emailAddress" });

	/** Effects */
	// The username is the address at nearly every hoster; it follows the address until the
	// person types their own.
	useEffect(() => {
		if (methods.formState.dirtyFields.imapUsername) return;
		methods.setValue("imapUsername", emailAddress ?? "");
	}, [emailAddress, methods]);

	/** Functions */
	function handleOnPresetChange(presetId: string | null) {
		const preset = IMAP_PRESETS.find((entry) => entry.id === presetId);
		if (!preset) return;
		methods.setValue("imapHost", preset.host, { shouldDirty: true });
		methods.setValue("imapPort", preset.port, { shouldDirty: true });
		methods.setValue("imapSecurity", preset.security, { shouldDirty: true });
	}

	function handleOnSecurityChange(security: MailboxFormValues["imapSecurity"]) {
		const current = methods.getValues("imapPort");
		const other = security === "tls" ? "starttls" : "tls";
		if (!current || current === DEFAULT_PORT[other]) {
			methods.setValue("imapPort", DEFAULT_PORT[security], { shouldDirty: true });
		}
	}

	/** Render */
	return (
		<div className="v-stack gap-6">
			<TextInput
				control={methods.control}
				name="label"
				label={t("mailboxes.label")}
				placeholder="Rechnungen"
				isRequired
				data-cy="mailbox-label-input"
			/>
			<TextInput
				control={methods.control}
				name="emailAddress"
				label={t("mailboxes.email_address")}
				placeholder={t("placeholders.email")}
				isRequired
				data-cy="mailbox-email-address-input"
			/>
			<Divider />
			<p className="label">{t("mailboxes.connection")}</p>
			<Select
				control={methods.control}
				name="preset"
				label={t("mailboxes.preset")}
				isRequired={false}
				isNullable
				options={IMAP_PRESETS.map((preset) => ({
					label: preset.label,
					value: preset.id,
					item: preset,
				}))}
				renderOption={(_, label) => <p>{label}</p>}
				itemNotFoundLabel={t("mailboxes.custom_preset")}
				onChange={(value) => handleOnPresetChange(value as string | null)}
				data-cy="mailbox-preset-select"
			/>
			<TextInput
				control={methods.control}
				name="imapHost"
				label={t("mailboxes.imap_host")}
				placeholder="imap.example.org"
				isRequired
				data-cy="mailbox-imap-host-input"
			/>
			<div className="grid grid-cols-2 gap-4">
				<Select
					control={methods.control}
					name="imapSecurity"
					label={t("mailboxes.imap_security")}
					isRequired
					isNullable={false}
					options={MAILBOX_IMAP_SECURITIES.map((security) => ({
						label: t(`mailboxes.security.${security}`),
						value: security,
						item: security,
					}))}
					renderOption={(_, label) => <p>{label}</p>}
					onChange={(value) => handleOnSecurityChange(value as MailboxFormValues["imapSecurity"])}
					data-cy="mailbox-imap-security-select"
				/>
				<NumberInput
					control={methods.control}
					name="imapPort"
					label={t("mailboxes.imap_port")}
					min={1}
					max={65535}
					isRequired
					data-cy="mailbox-imap-port-input"
				/>
			</div>
			<TextInput
				control={methods.control}
				name="imapUsername"
				label={t("mailboxes.imap_username")}
				placeholder={emailAddress || t("placeholders.email")}
				isRequired
				data-cy="mailbox-imap-username-input"
			/>
			<TextInput
				control={methods.control}
				name="password"
				type="password"
				label={t("mailboxes.password")}
				placeholder={isEdit ? t("mailboxes.password_unchanged_hint") : undefined}
				isRequired={!isEdit}
				data-cy="mailbox-password-input"
			/>
			<TextInput
				control={methods.control}
				name="folder"
				label={t("mailboxes.folder")}
				placeholder="INBOX"
				isRequired
				data-cy="mailbox-folder-input"
			/>
			<Divider />
			<Select
				control={methods.control}
				name="pollingIntervalMinutes"
				label={t("mailboxes.polling_interval")}
				isRequired
				isNullable={false}
				options={MAILBOX_POLLING_INTERVALS.map((minutes) => ({
					label: t("mailboxes.every_n_minutes", { count: String(minutes) }),
					value: minutes,
					item: minutes,
				}))}
				renderOption={(_, label) => <p>{label}</p>}
				data-cy="mailbox-polling-interval-select"
			/>
			{isEdit && (
				<FormSwitch
					control={methods.control}
					name="isPaused"
					data-cy="mailbox-is-paused-switch"
				>
					{t("mailboxes.is_paused")}
				</FormSwitch>
			)}
		</div>
	);
}
